import { Fragment, JSX } from 'react';
import { HIGHLIGHT_END, HIGHLIGHT_START } from 'types/globalSearch';

/** One stretch of a result's text, and whether the backend marked it as matched. */
export interface HighlightRun {
    text: string;
    highlighted: boolean;
}

/**
 * Splits a row's text on the highlight sentinels.
 *
 * The backend wraps matched terms in `[[hl]]`/`[[/hl]]` rather than markup precisely so nothing has
 * to be injected as HTML, and the producer escapes any sentinel the record's own text contained. A
 * sentinel that arrives unpaired anyway is kept as literal text: a record is never worth losing
 * characters over, and dropping them would hide exactly the text that provoked the bug.
 */
export function splitHighlightRuns(text: string): HighlightRun[] {
    const runs: HighlightRun[] = [];
    let cursor = 0;

    while (cursor < text.length) {
        const open = text.indexOf(HIGHLIGHT_START, cursor);
        const close = open === -1 ? -1 : text.indexOf(HIGHLIGHT_END, open + HIGHLIGHT_START.length);
        if (open === -1 || close === -1) {
            runs.push({ text: text.slice(cursor), highlighted: false });
            break;
        }

        if (open > cursor) {
            runs.push({ text: text.slice(cursor, open), highlighted: false });
        }
        const marked = text.slice(open + HIGHLIGHT_START.length, close);
        if (marked) {
            runs.push({ text: marked, highlighted: true });
        }
        cursor = close + HIGHLIGHT_END.length;
    }

    return runs;
}

/**
 * A result's text with its matched terms marked. The only highlighter in this feature — there is no
 * `dangerouslySetInnerHTML` anywhere in it, and adding one would hand a record's own text to the
 * HTML parser.
 */
export function HighlightedText({ text }: { text: string | null | undefined }): JSX.Element | null {
    if (!text) {
        return null;
    }

    return (
        <>
            {splitHighlightRuns(text).map((run, index) =>
                run.highlighted ? (
                    <mark key={index} className="bg-secondary/45 text-inherit">
                        {run.text}
                    </mark>
                ) : (
                    <Fragment key={index}>{run.text}</Fragment>
                )
            )}
        </>
    );
}
